import { mockLocations, mockStatus, mockWeather } from "./mock-data";
import type {
  EmergencyLinkState,
  LocationsResponse,
  SessionsResponse,
  StatusResponse,
  WeatherResponse
} from "./types";

const useMockData =
  import.meta.env.VITE_USE_LIVE_API !== "true" &&
  ["localhost", "127.0.0.1", ""].includes(window.location.hostname);

export function isMockMode(): boolean {
  return useMockData;
}

export async function fetchStatus(): Promise<StatusResponse> {
  if (useMockData) return mockStatus;

  const response = await fetch("/api/proxy/status", {
    headers: { accept: "application/json" },
    cache: "no-store"
  });
  if (!response.ok) throw new Error("Unable to load status");
  return response.json() as Promise<StatusResponse>;
}

export async function fetchWeather(
  latitude: number,
  longitude: number
): Promise<WeatherResponse> {
  if (useMockData) return mockWeather;

  const params = new URLSearchParams({
    lat: latitude.toString(),
    lon: longitude.toString()
  });
  const response = await fetch(`/api/proxy/weather?${params.toString()}`, {
    headers: { accept: "application/json" },
    cache: "no-store"
  });
  if (!response.ok) throw new Error("Unable to load weather");
  return response.json() as Promise<WeatherResponse>;
}

export async function fetchLocations(range: string): Promise<LocationsResponse> {
  if (useMockData) return mockLocations;

  const response = await fetch(`/api/proxy/locations?range=${encodeURIComponent(range)}`, {
    headers: { accept: "application/json" },
    cache: "no-store"
  });
  if (!response.ok) throw new Error("Unable to load locations");
  return response.json() as Promise<LocationsResponse>;
}

export async function fetchLocationsForSession(
  range: string,
  sessionId: string
): Promise<LocationsResponse> {
  if (useMockData) return mockLocations;

  const params = new URLSearchParams({ range, sessionId });
  const response = await fetch(`/api/proxy/locations?${params.toString()}`, {
    headers: { accept: "application/json" },
    cache: "no-store"
  });
  if (!response.ok) throw new Error("Unable to load session locations");
  return response.json() as Promise<LocationsResponse>;
}

export async function fetchSessions(range: string): Promise<SessionsResponse> {
  if (useMockData) {
    return {
      start: mockLocations.start,
      end: mockLocations.end,
      sessions: [
        {
          sessionId: "mock-session",
          startedAt: mockLocations.locations[0]!.recordedAt,
          endedAt: null,
          active: true,
          pointCount: mockLocations.locations.length
        }
      ]
    };
  }

  const response = await fetch(`/api/proxy/sessions?range=${encodeURIComponent(range)}`, {
    headers: { accept: "application/json" },
    cache: "no-store"
  });
  if (!response.ok) throw new Error("Unable to load sessions");
  return response.json() as Promise<SessionsResponse>;
}

export async function fetchCustomLocations(
  start: string,
  end: string
): Promise<LocationsResponse> {
  if (useMockData) return mockLocations;

  const params = new URLSearchParams({ start, end });
  const response = await fetch(`/api/proxy/locations?${params.toString()}`, {
    headers: { accept: "application/json" },
    cache: "no-store"
  });
  if (!response.ok) throw new Error("Unable to load custom range");
  return response.json() as Promise<LocationsResponse>;
}

export async function fetchEmergencyLinkState(): Promise<EmergencyLinkState> {
  if (useMockData) {
    return {
      active: false,
      createdAt: null,
      expiresAt: null,
      revokedAt: null,
      routeHours: 2
    };
  }

  const response = await fetch("/api/proxy/emergency-link", {
    headers: { accept: "application/json" },
    cache: "no-store"
  });
  if (!response.ok) throw new Error("Unable to load emergency link state");
  return response.json() as Promise<EmergencyLinkState>;
}

export async function createEmergencyLink(): Promise<EmergencyLinkState> {
  if (useMockData) {
    const expiresAt = new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString();
    return {
      active: true,
      createdAt: new Date().toISOString(),
      expiresAt,
      revokedAt: null,
      routeHours: 2,
      url: "https://example.test/emergency/mock-token"
    };
  }

  const response = await fetch("/api/proxy/emergency-link", {
    method: "POST",
    headers: { accept: "application/json" },
    cache: "no-store"
  });
  if (!response.ok) throw new Error("Unable to create emergency link");
  return response.json() as Promise<EmergencyLinkState>;
}

export async function revokeEmergencyLink(): Promise<EmergencyLinkState> {
  if (useMockData) {
    return {
      active: false,
      createdAt: null,
      expiresAt: null,
      revokedAt: new Date().toISOString(),
      routeHours: 2
    };
  }

  const response = await fetch("/api/proxy/emergency-link", {
    method: "DELETE",
    headers: { accept: "application/json" },
    cache: "no-store"
  });
  if (!response.ok) throw new Error("Unable to revoke emergency link");
  return response.json() as Promise<EmergencyLinkState>;
}
